import React, { useEffect, useMemo, useState } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { InputNumber } from "primereact/inputnumber";
import { Checkbox } from "primereact/checkbox";
import { Dropdown } from "primereact/dropdown";
import { Dialog } from "primereact/dialog";
import { Tag } from "primereact/tag";
import {
  apiService,
  Project,
  ProjectCreate,
  Client,
  Person,
  ProjectStatus,
  Activity,
  ActivityCreate,
  ServiceType,
} from "../../services/apiService";
import { useToast } from "../../contexts/ToastContext";
import { useSearch } from "../../contexts/SearchContext";
import { usePeopleContext } from "../../contexts/PeopleContext";
import { ActivityBadge, HourType } from "../../components/Badges";
import { InputIcon } from "primereact/inputicon";
import { IconField } from "primereact/iconfield";

const ProjectsAdmin: React.FC = () => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [projectStatuses, setProjectStatuses] = useState<ProjectStatus[]>([]);
  const [serviceTypes, setServiceTypes] = useState<ServiceType[]>([]);
  const [loading, setLoading] = useState(false);
  const { searchTerm: filter, setSearchTerm: setFilter } = useSearch();

  const toast = useToast();
  const { people } = usePeopleContext();
  const [projectModalVisible, setProjectModalVisible] = useState(false);
  const [editProject, setEditProject] = useState<Project | null>(null);
  const [formName, setFormName] = useState("");
  const [formClient, setFormClient] = useState<Client | null>(null);
  const [formManager, setFormManager] = useState<Person | null>(null);
  const [formStatus, setFormStatus] = useState<ProjectStatus | null>(null);
  const [formReservation, setFormReservation] = useState(false);
  const [formHourType, setFormHourType] = useState<
    "billable" | "non_billable" | "non_working"
  >("billable");
  const [formFeeTotal, setFormFeeTotal] = useState<number | null>(null);
  const [formFeeHourly, setFormFeeHourly] = useState<number | null>(null);

  // Activity modal
  const [activityModalVisible, setActivityModalVisible] = useState(false);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [editActivity, setEditActivity] = useState<Activity | null>(null);
  const [activityName, setActivityName] = useState("");
  const [activityServiceType, setActivityServiceType] =
    useState<ServiceType | null>(null);
  const [activityHourType, setActivityHourType] = useState<
    "billable" | "non_billable" | "non_working"
  >("billable");
  const [activitySchedulable, setActivitySchedulable] = useState(true);
  const [showActivityForm, setShowActivityForm] = useState(false);

  const hourTypeOptions = [
    { label: "Billable", value: "billable" },
    { label: "Non-Billable", value: "non_billable" },
    { label: "Non-Working", value: "non_working" },
  ];

  const load = async () => {
    setLoading(true);
    try {
      const [projectsRes, clientsRes, statusesRes, serviceTypesRes] =
        await Promise.all([
          apiService.getProjects(),
          apiService.getClients(),
          apiService.getProjectStatuses(),
          apiService.getServiceTypes(),
        ]);

      if (projectsRes.success) setProjects(projectsRes.data);
      if (clientsRes.success) setClients(clientsRes.data);
      if (statusesRes.success) setProjectStatuses(statusesRes.data);
      if (serviceTypesRes.success) setServiceTypes(serviceTypesRes.data);
    } catch (error) {
      toast.showError("Failed", "Unable to load projects");
    }
    setLoading(false);
  };

  // Sorted options for dropdowns
  const sortedClients = useMemo(
    () => [...clients].sort((a, b) => a.name.localeCompare(b.name)),
    [clients]
  );

  const sortedPeople = useMemo(
    () =>
      [...people].sort((a, b) =>
        `${a.name} ${a.surname}`.localeCompare(`${b.name} ${b.surname}`)
      ),
    [people]
  );

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(
    () =>
      projects.filter(
        (p) =>
          p.name.toLowerCase().includes(filter.toLowerCase()) ||
          p.reference?.toLowerCase().includes(filter.toLowerCase()) ||
          p.client.name.toLowerCase().includes(filter.toLowerCase())
      ),
    [projects, filter]
  );

  const openNewProject = () => {
    setEditProject(null);
    setFormName("");
    setFormClient(null);
    setFormManager(null);
    setFormStatus(null);
    setFormReservation(true);
    setFormHourType("billable");
    setFormFeeTotal(null);
    setFormFeeHourly(null);
    setProjectModalVisible(true);
  };

  const openEditProject = (project: Project) => {
    setEditProject(project);
    setFormName(project.name);
    setFormClient(project.client);
    setFormManager(project.manager || null);
    setFormStatus(project.status);
    setFormReservation(project.reservation);
    setFormHourType(project.hour_type);
    setFormFeeTotal(project.fee_total || null);
    setFormFeeHourly(project.fee_hourly || null);
    setProjectModalVisible(true);
  };

  const saveProject = async () => {
    if (!formName.trim() || !formClient || !formStatus) {
      toast.showWarn("Validation", "Name, client, and status are required");
      return;
    }

    const payload: ProjectCreate = {
      id: editProject?.id,
      name: formName.trim(),
      client: formClient,
      manager: formManager?.id,
      status: formStatus,
      reservation: formReservation,
      hour_type: formHourType,
      fee_total: formFeeTotal || undefined,
      fee_hourly: formFeeHourly || undefined,
    };

    const res = await apiService.createOrUpdateProject(payload);
    if (res.success) {
      toast.showSuccess("Saved", `Project ${res.data.reference} saved`);
      setProjectModalVisible(false);
      await load();
    } else {
      toast.showError("Error", res.message || "Save failed");
    }
  };

  const deleteProject = async (project: Project) => {
    const res = await apiService.deleteProject(project.id);
    if (res.success) {
      toast.showSuccess("Deleted", `Project '${project.name}' deleted`);
      await load();
    } else {
      toast.showError("Error", res.message || "Delete failed");
    }
  };

  const openActivities = (project: Project) => {
    setSelectedProject(project);
    setActivityModalVisible(true);
  };

  const updateProjectActivities = (
    projectId: number,
    updateActivities: (activities: Activity[]) => Activity[]
  ) => {
    setProjects((currentProjects) =>
      currentProjects.map((project) =>
        project.id === projectId
          ? {
              ...project,
              activities: updateActivities(project.activities || []),
            }
          : project
      )
    );
    setSelectedProject((currentProject) =>
      currentProject?.id === projectId
        ? {
            ...currentProject,
            activities: updateActivities(currentProject.activities || []),
          }
        : currentProject
    );
  };

  const openNewActivity = () => {
    setEditActivity(null);
    setActivityName("");
    setActivityServiceType(null);
    setActivityHourType("billable");
    setActivitySchedulable(true);
    setShowActivityForm(true);
  };

  const openEditActivity = (activity: Activity) => {
    setEditActivity(activity);
    setActivityName(activity.name);
    setActivityServiceType(
      serviceTypes.find((st) => st.id === activity.service_type_id) || null
    );
    setActivityHourType(activity.hour_type);
    setActivitySchedulable(activity.schedulable);
    setShowActivityForm(true);
  };

  const saveActivity = async () => {
    if (!activityName.trim() || !selectedProject) {
      toast.showWarn("Validation", "Activity name is required");
      return;
    }

    const payload: ActivityCreate = {
      id: editActivity?.id,
      name: activityName.trim(),
      hour_type: activityHourType,
      project_id: selectedProject.id,
      service_type_id: activityServiceType?.id,
      schedulable: activitySchedulable,
    };

    const res = await apiService.createOrUpdateActivity(payload);
    if (res.success) {
      toast.showSuccess("Saved", `Activity '${res.data.name}' saved`);
      setEditActivity(null);
      setActivityName("");
      setShowActivityForm(false);
      updateProjectActivities(selectedProject.id, (activities) => {
        const existingIndex = activities.findIndex(
          (activity) => activity.id === res.data.id
        );

        if (existingIndex === -1) {
          return [...activities, res.data];
        }

        return activities.map((activity) =>
          activity.id === res.data.id ? res.data : activity
        );
      });
    } else {
      toast.showError("Error", res.message || "Save failed");
    }
  };

  const deleteActivity = async (activity: Activity) => {
    const res = await apiService.deleteActivity(activity.id);
    if (res.success) {
      toast.showSuccess("Deleted", `Activity '${activity.name}' deleted`);
      updateProjectActivities(
        activity.project_id,
        (activities) =>
          activities.filter((currentActivity) => currentActivity.id !== activity.id)
      );
    } else {
      toast.showError("Error", res.message || "Delete failed");
    }
  };

  const renderActivities = (project: Project) => {
    if (!project.activities || project.activities.length === 0) {
      return <span className="text-muted-color">No activities</span>;
    }

    return (
      <div className="flex flex-wrap gap-1">
        {project.activities.slice(0, 3).map((activity) => (
          <ActivityBadge
            key={activity.id}
            value={activity.name}
            hourType={activity.hour_type as HourType}
            size="normal"
          />
        ))}
        {project.activities.length > 3 && (
          <Tag
            value={`+${project.activities.length - 3} more`}
            severity="info"
            className="text-xs"
          />
        )}
      </div>
    );
  };

  const header = (
    <div className="flex justify-content-between align-items-center">
      <div className="flex align-items-center gap-2">
        <IconField iconPosition="left">
          <InputIcon className="pi pi-search" />
          <InputText
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Search projects..."
          />
        </IconField>
      </div>
      <Button label="Add Project" icon="pi pi-plus" onClick={openNewProject} />
    </div>
  );

  return (
    <div>
      <DataTable
        value={filtered}
        loading={loading}
        header={header}
        emptyMessage="No projects found"
        className="p-datatable-sm"
        paginator
        rows={10}
      >
        <Column
          field="reference"
          header="Reference"
          sortable
          style={{ width: "120px" }}
        />
        <Column field="client.name" header="Client" sortable />
        <Column field="name" header="Project Title" sortable />
        <Column
          field="manager"
          header="Manager"
          body={(row: Project) =>
            row.manager ? `${row.manager.name} ${row.manager.surname}` : "—"
          }
          sortable
        />
        <Column
          field="status.name"
          header="Status"
          body={(row: Project) => (
            <Tag
              value={row.status.name}
              severity={
                row.status.completed
                  ? row.status.win
                    ? "success"
                    : "danger"
                  : "warning"
              }
            />
          )}
          sortable
        />
        <Column
          field="hour_type"
          header="Type"
          body={(row: Project) => (
            <Tag
              value={row.hour_type.replace("_", " ")}
              severity={
                row.hour_type === "billable"
                  ? "success"
                  : row.hour_type === "non_billable"
                  ? "warning"
                  : "danger"
              }
            />
          )}
          sortable
        />
        <Column
          field="reservation"
          header="Reservation"
          body={(row: Project) => (
            <i className={`pi ${row.reservation ? "pi-check" : "pi-times"}`} />
          )}
          sortable
          style={{ width: "120px" }}
        />
        <Column
          header="Activities"
          body={renderActivities}
          style={{ width: "200px" }}
        />
        <Column
          header="Actions"
          body={(row: Project) => (
            <div className="flex gap-2 justify-content-end">
              <Button
                icon="pi pi-list"
                className="p-button-rounded p-button-outlined p-button-sm"
                onClick={() => openActivities(row)}
                tooltip="Manage Activities"
              />
              <Button
                icon="pi pi-pencil"
                className="p-button-rounded p-button-outlined p-button-sm"
                onClick={() => openEditProject(row)}
              />
              <Button
                icon="pi pi-trash"
                className="p-button-rounded p-button-outlined p-button-sm p-button-danger"
                onClick={() => deleteProject(row)}
              />
            </div>
          )}
          style={{ width: "140px" }}
          headerStyle={{ textAlign: "right" }}
        />
      </DataTable>

      {/* Project Modal */}
      <Dialog
        header={editProject ? "Edit Project" : "Add Project"}
        visible={projectModalVisible}
        onHide={() => setProjectModalVisible(false)}
        style={{ width: 600 }}
        modal
      >
        <div className="flex flex-column gap-3">
          <div className="field">
            <label className="block mb-2">Project Name *</label>
            <InputText
              value={formName}
              onChange={(e) => setFormName(e.target.value)}
              className="w-full"
            />
          </div>

          <div className="grid">
            <div className="col-6">
              <div className="field">
                <label className="block mb-2">Client *</label>
                <Dropdown
                  value={formClient}
                  onChange={(e) => setFormClient(e.value)}
                  options={sortedClients}
                  optionLabel="name"
                  placeholder="Select a client"
                  className="w-full"
                  filter
                  filterBy="name"
                  showClear
                  filterInputAutoFocus
                />
              </div>
            </div>
            <div className="col-6">
              <div className="field">
                <label className="block mb-2">Manager</label>
                <Dropdown
                  value={formManager}
                  onChange={(e) => setFormManager(e.value)}
                  options={sortedPeople}
                  optionLabel="name"
                  itemTemplate={(person: Person) =>
                    `${person.name} ${person.surname}`
                  }
                  valueTemplate={(person: Person) =>
                    person
                      ? `${person.name} ${person.surname}`
                      : "Select a manager"
                  }
                  placeholder="Select a manager"
                  className="w-full"
                  filter
                  filterBy="name,surname"
                  showClear
                  filterInputAutoFocus
                />
              </div>
            </div>
          </div>

          <div className="grid">
            <div className="col-4">
              <div className="field">
                <label className="block mb-2">Type *</label>
                <Dropdown
                  value={formHourType}
                  onChange={(e) => setFormHourType(e.value)}
                  options={hourTypeOptions}
                  className="w-full"
                />
              </div>
            </div>
            <div className="col-4">
              <div className="field">
                <label className="block mb-2">Project Status *</label>
                <Dropdown
                  value={formStatus}
                  onChange={(e) => setFormStatus(e.value)}
                  options={projectStatuses}
                  optionLabel="name"
                  placeholder="Select status"
                  className="w-full"
                />
              </div>
            </div>
            <div className="col-4">
              <div className="field flex align-items-center gap-2 mt-4">
                <Checkbox
                  inputId="project-reservation"
                  checked={formReservation}
                  onChange={(e) => setFormReservation(!!e.checked)}
                />
                <label htmlFor="project-reservation">Reservation</label>
              </div>
            </div>
          </div>

          <div className="grid">
            <div className="col-6">
              <div className="field">
                <label className="block mb-2">Total Fee Expected</label>
                <InputNumber
                  value={formFeeTotal}
                  onValueChange={(e) => setFormFeeTotal(e.value ?? null)}
                  mode="currency"
                  currency="EUR"
                  locale="nl-NL"
                  className="w-full"
                />
              </div>
            </div>
            <div className="col-6">
              <div className="field">
                <label className="block mb-2">Hourly Fee Expected</label>
                <InputNumber
                  value={formFeeHourly}
                  onValueChange={(e) => setFormFeeHourly(e.value ?? null)}
                  mode="currency"
                  currency="EUR"
                  locale="nl-NL"
                  className="w-full"
                />
              </div>
            </div>
          </div>

          <div className="flex justify-content-end gap-2 mt-3">
            <Button
              label="Cancel"
              className="p-button-text"
              onClick={() => setProjectModalVisible(false)}
            />
            <Button label="Save" icon="pi pi-check" onClick={saveProject} />
          </div>
        </div>
      </Dialog>

      {/* Activities Modal */}
      <Dialog
        header={
          selectedProject
            ? `Activities for ${selectedProject.name}`
            : "Manage Activities"
        }
        visible={activityModalVisible}
        onHide={() => setActivityModalVisible(false)}
        style={{ width: 800 }}
        modal
      >
        {selectedProject && (
          <div className="flex flex-column gap-4">
            <div className="flex justify-content-between align-items-center">
              <h5>Activities</h5>
              <Button
                label="Add Activity"
                icon="pi pi-plus"
                onClick={openNewActivity}
                size="small"
              />
            </div>

            <DataTable
              value={selectedProject.activities || []}
              emptyMessage="No activities found"
              className="p-datatable-sm"
            >
              <Column field="name" header="Activity Name" />
              <Column
                field="hour_type"
                header="Type"
                body={(row: Activity) => (
                  <ActivityBadge
                    value={row.hour_type.replace("_", " ")}
                    hourType={row.hour_type as HourType}
                    size="normal"
                  />
                )}
              />
              <Column
                field="schedulable"
                header="Schedulable"
                body={(row: Activity) => (
                  <i
                    className={`pi ${
                      row.schedulable ? "pi-check" : "pi-times"
                    }`}
                  />
                )}
              />
              <Column
                header="Actions"
                body={(row: Activity) => (
                  <div className="flex gap-2">
                    <Button
                      icon="pi pi-pencil"
                      className="p-button-rounded p-button-outlined p-button-sm"
                      onClick={() => openEditActivity(row)}
                    />
                    <Button
                      icon="pi pi-trash"
                      className="p-button-rounded p-button-outlined p-button-sm p-button-danger"
                      onClick={() => deleteActivity(row)}
                    />
                  </div>
                )}
                style={{ width: "100px" }}
              />
            </DataTable>

            {/* Activity Form */}
            {(editActivity || showActivityForm) && (
              <div className="surface-100 p-3 border-round">
                <h6>{editActivity ? "Edit Activity" : "New Activity"}</h6>
                <div className="flex flex-column gap-3">
                  <div className="grid">
                    <div className="col-4">
                      <div className="field">
                        <label className="block mb-2">Activity Name *</label>
                        <InputText
                          value={activityName}
                          onChange={(e) => setActivityName(e.target.value)}
                          className="w-full"
                        />
                      </div>
                    </div>
                    <div className="col-3">
                      <div className="field">
                        <label className="block mb-2">Service Type</label>
                        <Dropdown
                          value={activityServiceType}
                          onChange={(e) => setActivityServiceType(e.value)}
                          options={serviceTypes}
                          optionLabel="name"
                          placeholder="Select service type"
                          className="w-full"
                        />
                      </div>
                    </div>
                    <div className="col-3">
                      <div className="field">
                        <label className="block mb-2">Type *</label>
                        <Dropdown
                          value={activityHourType}
                          onChange={(e) => setActivityHourType(e.value)}
                          options={hourTypeOptions}
                          className="w-full"
                        />
                      </div>
                    </div>
                    <div className="col-2">
                      <div className="field flex align-items-center gap-2 mt-4">
                        <Checkbox
                          inputId="activity-schedulable"
                          checked={activitySchedulable}
                          onChange={(e) => setActivitySchedulable(!!e.checked)}
                        />
                        <label htmlFor="activity-schedulable">
                          Schedulable
                        </label>
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-content-end gap-2">
                    <Button
                      label="Cancel"
                      className="p-button-text"
                      onClick={() => {
                        setEditActivity(null);
                        setActivityName("");
                        setActivityServiceType(null);
                        setShowActivityForm(false);
                      }}
                    />
                    <Button
                      label="Save Activity"
                      icon="pi pi-check"
                      onClick={saveActivity}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </Dialog>
    </div>
  );
};

export default ProjectsAdmin;
